/**
 * RevendasTableRow - Linha da tabela de revendas
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Chip,
  Checkbox,
  Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CollectionActions from './CollectionActions';

const RevendasTableRow = memo(({
  row,
  themeMode,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const getLogo = () => `/api/revendas/logo/${row.code}?theme=${themeMode}`;

  const statusColors = row.disabled
    ? { bg: tokens?.semantic?.errorBg, color: tokens?.semantic?.error, border: `${tokens?.semantic?.error}40` }
    : { bg: tokens?.semantic?.successBg, color: tokens?.semantic?.success, border: `${tokens?.semantic?.success}40` };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar/Logo */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          alt={row.title}
          src={getLogo()}
          sx={{ width: 32, height: 32 }}
        >
          {row.title?.charAt(0)?.toUpperCase() || 'R'}
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Code */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.code || '-'}
        </Typography>
      </TableCell>

      {/* Title/Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {row.title}
        </Typography>
      </TableCell>

      {/* City */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.city || '-'}
        </Typography>
      </TableCell>

      {/* State */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.state || '-'}
        </Typography>
      </TableCell>

      {/* Clients Count */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={row.clientsCount || 0}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: tokens?.semantic?.infoBg,
            color: tokens?.semantic?.info,
            border: `1px solid ${tokens?.semantic?.info}40`,
          }}
        />
      </TableCell>

      {/* Status */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')}
          size="small"
          className={row.disabled ? 'status-disabled' : 'status-active'}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.color,
            border: `1px solid ${statusColors.border}`,
          }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <CollectionActions
            itemId={row.id}
            editPath="/settings/revenda"
            endpoint="revendas"
            setTimestamp={onRefresh}
            customActions={[
              {
                key: 'viewClients',
                title: t('revendaViewClients'),
                icon: <VisibilityIcon />,
                variant: 'info',
                handler: (id) => navigate(`/settings/revenda/${id}/clients`),
              },
            ]}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
});

RevendasTableRow.displayName = 'RevendasTableRow';

export default RevendasTableRow;
